import type {
  ChangeEventHandler,
  FocusEventHandler,
  KeyboardEventHandler,
  RefObject,
} from 'react';
import { useEffect, useState } from 'react';

import { useIsomorphicLayoutEffect } from '../../../hooks/useIsomorphicLayoutEffect';
import type {
  InputCleanMaskFn,
  InputMask,
  InputMaskFn,
  InputProps,
  InputWithMask,
} from '../types';
import { isMaskedInput } from '../types';
import { cleanPhoneMask, maskPhone } from './maskPhone';
import { cleanZipMask, maskZip } from './maskZip';

function getMaskFnsFromProps(props: InputWithMask) {
  // Maps mask functions to the value of the `mask` prop.
  const maskFns: Record<InputMask, InputMaskFn> = {
    zip: maskZip,
    phone: maskPhone,
  };

  // Maps cleaning mask functions to the value of the `mask` prop.
  const cleanMaskFns: Record<InputMask, InputCleanMaskFn> = {
    zip: cleanZipMask,
    phone: cleanPhoneMask,
  };
  let { mask } = props;
  if (typeof mask === 'string') {
    mask = [maskFns[mask], cleanMaskFns[mask]];
  }
  return mask;
}

export interface UseInputMask {
  value: string;
  onBlur: FocusEventHandler<HTMLInputElement>;
  onChange: ChangeEventHandler<HTMLInputElement>;
  onKeyDown: KeyboardEventHandler<HTMLInputElement>;
}

/** Return props for the input HTML element when a mask is applied. */
export function useInputMask(
  /** A ref object to the input HTML element. */
  inputRef: RefObject<HTMLInputElement>,
  /** The props passed to the `Input` component. */
  inputProps: InputProps
): UseInputMask {
  /** Function to mask the value. */
  let getMaskedValue: InputMaskFn | undefined = undefined;
  /** Function to clean the masked value when pressing backspace/ */
  let cleanMaskedValue: InputCleanMaskFn | undefined = undefined;

  const isMasked = isMaskedInput(inputProps);

  if (isMasked) {
    [getMaskedValue, cleanMaskedValue] = getMaskFnsFromProps(inputProps);
  }
  const [initialValue] = getMaskedValue?.(inputProps.value ?? '') ?? [''];
  const [value, setValue] = useState(initialValue);

  // Store the cursor position when a mask is applied
  const [cursor, setCursor] = useState<number | undefined>();

  useEffect(() => {
    if (!isMasked) {
      return;
    }
    const [newMaskedValue] = getMaskedValue?.(inputProps.value ?? '') ?? [''];
    setValue(newMaskedValue);
  }, [isMasked, getMaskedValue, inputProps.value]);

  // Restore cursor position after render when a mask is applied
  useIsomorphicLayoutEffect(() => {
    if (!isMasked) {
      return;
    }
    if (!inputRef.current) {
      return;
    }
    if (cursor === undefined) {
      return;
    }
    inputRef.current.setSelectionRange(cursor, cursor);
  }, [cursor, inputProps.type, isMasked, inputRef]);

  const onBlur: FocusEventHandler<HTMLInputElement> = () => {
    setCursor(undefined);
  };

  const onChange: ChangeEventHandler<HTMLInputElement> = e => {
    if (!getMaskedValue) {
      return;
    }
    const { selectionStart } = e.target;
    const [newValue, position] = getMaskedValue(
      e.target.value,
      selectionStart ?? 0
    );
    setCursor(position);
    setValue(newValue);
    e.target.value = newValue;
  };

  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = e => {
    if (!cursor) {
      return;
    }
    if (e.key !== 'Backspace') {
      return;
    }
    if (!cleanMaskedValue) {
      return;
    }
    // Clean the mask from the extra chars added during masking
    setValue(cleanMaskedValue(value));
  };

  return { value, onBlur, onChange, onKeyDown };
}
